import React, { useState, useEffect } from 'react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Trash2, X, Edit2, Search, CheckSquare, Square,
  Tag, Power, PowerOff
} from 'lucide-react';

const BrandsManager = () => {
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  const [confirmInactivate, setConfirmInactivate] = useState({ show: false, brandId: null });
  const [confirmDelete, setConfirmDelete] = useState({ show: false, brandId: null });
  const [confirmInactivateSelected, setConfirmInactivateSelected] = useState({ show: false });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const response = await inventoryService.getBrands();
      if (response.success) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      toast.error('Erro ao carregar marcas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadBrands();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome da marca é obrigatório');
      return;
    }

    try {
      if (editingBrand) {
        await inventoryService.updateBrand(editingBrand.id, formData);
        toast.success('Marca atualizada com sucesso!');
      } else {
        await inventoryService.createBrand(formData);
        toast.success('Marca criada com sucesso!');
      }

      resetForm();
      loadBrands();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar marca');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      nome: brand.nome || '',
      descricao: brand.descricao || '',
      ativo: brand.ativo
    });
    setShowForm(true);
  };

  // Inativar marca
  const handleInactivate = async (id) => {
    setConfirmInactivate({ show: true, brandId: id });
  };

  const confirmInactivateAction = async () => {
    const { brandId } = confirmInactivate;
    setConfirmInactivate({ show: false, brandId: null });

    try {
      await inventoryService.deleteBrand(brandId);
      toast.success('Marca inativada com sucesso!');
      loadBrands();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao inativar marca');
    }
  };

  // Ativar marca
  const handleActivate = async (id) => {
    try {
      await inventoryService.updateBrand(id, { ativo: true });
      toast.success('Marca ativada com sucesso!');
      loadBrands();
    } catch (error) {
      toast.error('Erro ao ativar marca');
    }
  };

  // Excluir definitivamente
  const handleDelete = async (id) => {
    setConfirmDelete({ show: true, brandId: id });
  };

  const confirmDeleteAction = async () => {
    const { brandId } = confirmDelete;
    setConfirmDelete({ show: false, brandId: null });

    try {
      await inventoryService.deleteBrand(brandId);
      toast.success('Marca excluída com sucesso!');
      loadBrands();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao excluir marca';
      toast.error(msg);
    }
  };

  // Seleção individual
  const toggleSelectBrand = (id) => {
    setSelectedBrands(prev =>
      prev.includes(id)
        ? prev.filter(b => b !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    if (selectedBrands.length === filteredBrands.length) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands(filteredBrands.map(b => b.id));
    }
  };

  // Inativar selecionados
  const handleInactivateSelected = async () => {
    if (selectedBrands.length === 0) {
      toast.error('Nenhuma marca selecionada');
      return;
    }

    setConfirmInactivateSelected({ show: true });
  };

  const confirmInactivateSelectedAction = async () => {
    setConfirmInactivateSelected({ show: false });

    try {
      setLoading(true);
      for (const id of selectedBrands) {
        await inventoryService.deleteBrand(id);
      }
      toast.success(`${selectedBrands.length} marca(s) inativada(s) com sucesso!`);
      setSelectedBrands([]);
      loadBrands();
    } catch (error) {
      toast.error('Erro ao inativar marcas');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar marcas
  const filteredBrands = brands.filter(brand => {
    const matchesSearch = !search ||
      brand.nome.toLowerCase().includes(search.toLowerCase()) ||
      (brand.descricao && brand.descricao.toLowerCase().includes(search.toLowerCase()));

    if (showInactive) {
      return matchesSearch;
    }
    return matchesSearch && brand.ativo;
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingBrand(null);
    setFormData({
      nome: '',
      descricao: '',
      ativo: true
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marcas</h1>
          <p className="text-gray-600">Gerencie as marcas de produtos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Marca
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Mostrar inativas</span>
        </label>

        {selectedBrands.length > 0 && (
          <button
            onClick={handleInactivateSelected}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <PowerOff className="w-4 h-4" />
            Inativar ({selectedBrands.length})
          </button>
        )}
      </div>

      {/* Brands List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedBrands.length === filteredBrands.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedBrands.length === filteredBrands.length && filteredBrands.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : filteredBrands.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Nenhuma marca encontrada
                </td>
              </tr>
            ) : (
              filteredBrands.map((brand) => (
                <tr key={brand.id} className={`hover:bg-gray-50 ${selectedBrands.includes(brand.id) ? 'bg-blue-50' : ''} ${!brand.ativo ? 'bg-gray-100' : ''}`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleSelectBrand(brand.id)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      {selectedBrands.includes(brand.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Tag className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-sm font-medium text-gray-900">{brand.nome}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {brand.descricao || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      brand.ativo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {brand.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {brand.ativo ? (
                        <button
                          onClick={() => handleInactivate(brand.id)}
                          className="text-orange-600 hover:text-orange-800 p-1"
                          title="Inativar"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(brand.id)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Ativar"
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(brand)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(brand.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir definitivamente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingBrand ? 'Editar Marca' : 'Nova Marca'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Marca *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Blum, Hafele, Duratex"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Descrição adicional da marca..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Marca ativa
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingBrand ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Inactivate Modal */}
      <ConfirmModal
        show={confirmInactivate.show}
        onConfirm={confirmInactivateAction}
        onCancel={() => setConfirmInactivate({ show: false, brandId: null })}
        title="Inativar Marca"
        message="Tem certeza que deseja inativar esta marca?"
        confirmText="Inativar"
        type="warning"
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        show={confirmDelete.show}
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ show: false, brandId: null })}
        title="Excluir Marca"
        message="ATENÇÃO: Exclusão definitiva! A marca será removida permanentemente. Continuar?"
        confirmText="Excluir"
        type="danger"
      />

      {/* Confirm Inactivate Selected Modal */}
      <ConfirmModal
        show={confirmInactivateSelected.show}
        onConfirm={confirmInactivateSelectedAction}
        onCancel={() => setConfirmInactivateSelected({ show: false })}
        title="Inativar Marcas"
        message={`Tem certeza que deseja inativar ${selectedBrands.length} marca(s)?`}
        confirmText="Inativar"
        type="warning"
      />
    </div>
  );
};

export default BrandsManager;
